import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { ThumbsUp, MessageCircle, Share2, MoreHorizontal, BarChart3, Loader2 } from "lucide-react";
import ImageView from "../ImageView";
import CanvaView from "../CanvaView";
import { getAppLink, getCurrentFacebookMe, getCurrentFacebookPages } from "../../../api/AppLinkApi";
import { getBaseUrl } from "../../../api/api";
import { timeAgo } from "../../helpers";
import RuleAction from "../../../actions/RuleAction";

interface Props {
  postType: string;
  content: string;
  scheduledDateTime: Date;
  mediaId: string;
  mediaType: "HOST" | "CANVA";
  engagement: any;
  platformPostId?: string;
  onToggleInsights?: () => void;
}

export default function FacebookPost({
  postType,
  content,
  scheduledDateTime,
  mediaId,
  mediaType,
  engagement,
  platformPostId: _platformPostId,
  onToggleInsights,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [displayName, setDisplayName] = useState(t("Anonymous"));
  const [avatarUrl, setAvatarUrl] = useState();
  const [username, setUsername] = useState<string>("");
  const [defaultPageName, setDefaultPageName] = useState<string>("");
  const [, setDefaultPageId] = useState<string>("");
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount

  const loadProfile = () => {
    getCurrentFacebookMe().then((data) => {
      setDisplayName(data?.name || t("Anonymous"));
      setAvatarUrl(data?.pictureUrl);
      setUsername(data?.email || "");
    });

    getAppLink("facebook")
      .then((appLink) => {
        const pageId = appLink?.data?.page_id || "";
        setDefaultPageId(pageId);
        if (!pageId) return;

        return getCurrentFacebookPages().then((pages) => {
          const page = (pages || []).find((p: any) => String(p?.id) === String(pageId));
          const pageName = page?.name || "";
          setDefaultPageName(pageName);

          if (page) {
            dispatch(
              RuleAction.setPlatformProfile({
                platformId: "facebook-page",
                profile: {
                  id: page.id,
                  name: page.name,
                  picture: page.pictureUrl || "",
                  username: page.id,
                },
              })
            );
          }
        });
      })
      .catch(() => {
        setDefaultPageId("");
        setDefaultPageName("");
      });
  };

  useEffect(() => {
    // Reset loading state when mediaId or mediaType changes
    if (mediaId && postType === "image") {
      setImageLoading(true);
      setImageError(false);
    } else {
      setImageLoading(false);
    }
  }, [mediaId, mediaType, postType]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
  };

  const angry = engagement.getIn(["metrics", "reactions_angry", "summary", "total_count"]) || 0;
  const haha = engagement.getIn(["metrics", "reactions_haha", "summary", "total_count"]) || 0;
  const like = engagement.getIn(["metrics", "reactions_like", "summary", "total_count"]) || 0;
  const love = engagement.getIn(["metrics", "reactions_love", "summary", "total_count"]) || 0;
  const sad = engagement.getIn(["metrics", "reactions_sad", "summary", "total_count"]) || 0;
  const wow = engagement.getIn(["metrics", "reactions_wow", "summary", "total_count"]) || 0;
  const total = angry + haha + like + love + sad + wow;

  const reactionList = [
    { name: "angry", value: "😡", count: angry },
    { name: "haha", value: "😄", count: haha },
    { name: "like", value: "👍", count: like },
    { name: "love", value: "❤️", count: love },
    { name: "sad", value: "😢", count: sad },
    { name: "wow", value: "😮", count: wow },
  ].sort((a, b) => b.count - a.count);

  const renderReaction = (
    <div className="flex -space-x-1 overflow-hidden justify-start">
      {reactionList.map((item, index) => {
        if (item.count === 0) return null;
        return (
          <div
            key={`key-${item.name}`}
            className="relative inline-block rounded-full bg-white"
            style={{ zIndex: reactionList.length - index }}
          >
            {item.value}
          </div>
        );
      })}
      {total === 0 ? <div className="relative inline-block rounded-full bg-white">👍</div> : null}
    </div>
  );

  return (
    <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 mt-4">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center space-x-3">
          {avatarUrl ? (
            <img src={avatarUrl} alt="avatar" className="w-10 h-10 rounded-full" />
          ) : (
            <div className="w-10 h-10 rounded-full">
              <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          )}
          <div>
            <h3 className="font-semibold text-gray-900">{displayName}</h3>
            {username && <p className="text-xs text-gray-500 truncate">{username}</p>}
            {defaultPageName && (
              <p className="text-xs font-semibold text-gray-500 truncate">
                {t("Page")}: {defaultPageName}
              </p>
            )}
            <p className="text-sm text-gray-500">{timeAgo(scheduledDateTime)} · 🌍 Public</p>
          </div>
        </div>
        <MoreHorizontal className="text-gray-500" />
      </div>

      {/* Content */}
      <div className="px-4 pb-2">
        <p className="text-gray-800 mb-3">{content}</p>
        {postType === "image" ? (
          <div
            className="relative w-full bg-gray-100 rounded-lg overflow-hidden"
            style={{ minHeight: "200px", maxHeight: "600px" }}
          >
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
            )}
            {imageError && !imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="text-center text-gray-400">
                  <p className="text-sm">{t("Failed to load image")}</p>
                </div>
              </div>
            )}
            {mediaId ? (
              <>
                {mediaType === "CANVA" ? (
                  <div
                    className={`w-full ${
                      imageLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300"
                    }`}
                  >
                    <CanvaView
                      fileId={mediaId}
                      className="w-full h-auto object-contain"
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />
                  </div>
                ) : (
                  <div
                    className={`w-full ${
                      imageLoading ? "opacity-0" : "opacity-100 transition-opacity duration-300"
                    }`}
                  >
                    <ImageView
                      fileName={mediaId}
                      className="w-full h-auto object-contain"
                      onLoad={handleImageLoad}
                      onError={handleImageError}
                    />
                  </div>
                )}
              </>
            ) : (
              <img
                src={`${getBaseUrl()}/static/images/upload-images.png`}
                alt=""
                className="w-full h-auto object-cover"
                onLoad={handleImageLoad}
                onError={handleImageError}
              />
            )}
          </div>
        ) : null}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between px-4 py-2 text-sm text-gray-600 border-t border-gray-100">
        <div className="flex items-center space-x-1">
          {renderReaction}
          <span>{total}</span>
        </div>
        <div className="flex items-center space-x-3">
          <span>{`${
            engagement.getIn(["metrics", "comments", "summary", "total_count"]) || 0
          } Comments`}</span>
          <span>{`${
            engagement.getIn(["metrics", "shares", "summary", "total_count"]) || 0
          } Shares`}</span>
          {onToggleInsights && (
            <button
              onClick={onToggleInsights}
              className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium cursor-pointer"
              title={t("View Insights")}
            >
              <BarChart3 className="w-4 h-4" />
              <span>{t("Insights")}</span>
            </button>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-around border-t border-gray-100 text-gray-700 text-sm">
        <button className="flex items-center justify-center gap-2 py-2 w-full hover:bg-gray-100 rounded-lg transition">
          <ThumbsUp className="w-5 h-5" />
          {t("Like")}
        </button>
        <button className="flex items-center justify-center gap-2 py-2 w-full hover:bg-gray-100 rounded-lg transition">
          <MessageCircle className="w-5 h-5" />
          {t("Comment")}
        </button>
        <button className="flex items-center justify-center gap-2 py-2 w-full hover:bg-gray-100 rounded-lg transition">
          <Share2 className="w-5 h-5" />
          {t("Share")}
        </button>
      </div>

      {/* Comment input */}
      <div className="flex items-center gap-2 p-4 border-t border-gray-100">
        {avatarUrl ? (
          <img src={avatarUrl} alt="avatar" className="w-9 h-9 rounded-full" />
        ) : (
          <div className="w-9 h-9 rounded-full">
            <svg className="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </div>
        )}
        <input
          type="text"
          disabled
          placeholder={t("Write a comment...")}
          className="flex-1 bg-gray-100 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
        />
      </div>
    </div>
  );
}
